import { ArrowLeft, House, SignOut } from "@phosphor-icons/react";
import { motion } from "motion/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks";
import AnimatedModal from "../components/AnimatedModal";
import { MeshBackground, useGlobalStyles } from "./AuthShared";
import ThemeToggle from "../components/ThemeToggle";

const EASE = [0.22, 1, 0.36, 1];

const NotFound: React.FC = () => {
  useGlobalStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, permittedRoutes, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    setShowLogoutModal(false);
    logout();
  };

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col text-gray-900 dark:text-gray-100 dark:bg-neutral-950 [font-family:var(--font-geist)]">
      <MeshBackground />

      <div className="absolute top-4 right-4 z-[10]">
        <ThemeToggle />
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 z-[2]">
        <div className="w-full text-center" style={{ maxWidth: "clamp(360px, 90vw, 540px)" }}>

          {/* 404 gradient number */}
          <motion.div
            className="arb-gradient-text text-[clamp(96px,18vw,160px)] font-extrabold leading-none tracking-[-0.05em] mb-4 select-none"
            style={{ backgroundImage: "linear-gradient(135deg, #1a3d7c 0%, #30cd73 100%)" }}
            initial={{ opacity: 0, scale: 0.8, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            {t("notFound.title")}
          </motion.div>

          {/* Heading */}
          <motion.h2
            className="text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 mt-0 mb-3"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.18, ease: EASE }}
          >
            {t("notFound.heading")}
          </motion.h2>

          {/* Description */}
          <motion.p
            className="text-[13.5px] text-gray-500 dark:text-neutral-400 leading-relaxed mb-8 max-w-sm mx-auto"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.28, ease: EASE }}
          >
            {t("notFound.description")}
          </motion.p>

          {/* Action buttons — staggered */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <motion.button
              type="button"
              onClick={() => navigate(user ? (permittedRoutes[0] || {}).path || "/" : "/")}
              className="flex items-center gap-2 px-5 py-3 rounded-[10px] text-[13.5px] font-semibold bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-[0_2px_14px_rgba(26,61,124,0.28)] hover:shadow-[0_4px_20px_rgba(26,61,124,0.38)] hover:-translate-y-px active:translate-y-0 transition-all duration-200 cursor-pointer border-none [font-family:inherit] min-h-[44px]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.38, ease: EASE }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              <House size={16} weight="duotone" aria-hidden />
              {t("notFound.goHome")}
            </motion.button>

            <motion.button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-5 py-3 rounded-[10px] text-[13.5px] font-medium bg-white/[0.7] dark:bg-neutral-800/80 border border-black/[0.09] dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-neutral-700 hover:-translate-y-px active:translate-y-0 transition-all duration-200 cursor-pointer [font-family:inherit] min-h-[44px] backdrop-blur-sm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.46, ease: EASE }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              <ArrowLeft size={16} weight="regular" aria-hidden />
              {t("notFound.goBack")}
            </motion.button>

            <motion.button
              type="button"
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-[10px] text-[13.5px] font-medium bg-red-50/80 dark:bg-red-900/20 border border-red-200/60 dark:border-red-800/40 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 hover:-translate-y-px active:translate-y-0 transition-all duration-200 cursor-pointer [font-family:inherit] min-h-[44px]"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.54, ease: EASE }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              <SignOut size={16} weight="regular" aria-hidden />
              {t("common.logout")}
            </motion.button>
          </div>
        </div>
      </div>

      <AnimatedModal
        isConfirmation
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        title={t("common.confirm_logout")}
        confirmText={t("common.logout")}
        confirmButtonColor="red"
      >
        <div className="text-gray-700 dark:text-gray-300 text-center">
          {t("common.logout_confirmation_message")}
        </div>
      </AnimatedModal>
    </div>
  );
};

export default NotFound;
